import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const MuraRootCauseLabeling = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const muraImage = location.state?.muraImage || localStorage.getItem("muraImage");
  const muraType = location.state?.muraType || "Not Selected";
  const [selectedMuraType, setSelectedMuraType] = useState("");
  const [selectedCause, setSelectedCause] = useState(null);
  const [customCause, setCustomCause] = useState("");
  const [issueType, setIssueType] = useState("");
  const [isCustomType, setIsCustomType] = useState(false);

useEffect(() => {
  if (location.state?.muraType) {
    setSelectedMuraType(location.state.muraType);
    if (location.state?.issueType) {
      setIssueType(location.state.issueType);
      setIsCustomType(location.state.issueType === "MuraLabeling" && location.state.muraType.toLowerCase() === "other");
    } else {
      const muraTypeNormalized = location.state.muraType.toLowerCase().trim().replace(/\s+/g, " ");
      if (muraTypeNormalized === "pressing light leakage") {
        setIssueType("LightLeakage");
        setIsCustomType(false);
      } else if (muraTypeNormalized === "other") {
        setIssueType("MuraLabeling");
        setIsCustomType(true);
      } else {
        setIssueType("MuraLabeling");
        setIsCustomType(false);
      }
    }
  }
}, [location.state]);

  const muraLabelingCauses = [
    "OC Issue",
    "LB Issue",
    "Film Issue",
    "Diffuser Plate",
    "Reflect Sheet",
    "Back Cover",
    "Front Cover",
    "Middle Cover",
    "Deco",
    "Spacer",
    "Others",
  ];

  const handleIssueTypeChange = (e) => {
    setIssueType(e.target.value);
    setSelectedCause(null);
    setCustomCause("");
  };

  const handleSelection = (cause) => {
    if (!issueType) {
      Swal.fire({
        title: "Issue Type Required",
        text: "Please select an issue type (MuraLabeling or LightLeakage) before selecting a root cause.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
      });
      return;
    }
    if (cause === selectedCause) {
      setSelectedCause(null);
      setCustomCause("");
    } else {
      setSelectedCause(cause);
      if (cause !== "Others") {
        setCustomCause("");
      }
    }
  };

const handleBack = () => {
  navigate("/mark-defect-region", {
    state: {
      uploadedImage: location.state?.uploadedImage || muraImage,
      recordedEntries: location.state?.recordedEntries || [],
      selectedMuraType,
      customMuraType: location.state?.customMuraType || "",
      rectangles: location.state?.rectangles || [], 
      customMuraTypes: location.state?.customMuraTypes || {},
      fromMuraLabeling: location.state?.fromMuraLabeling || false,
      yoloAnnotations: location.state?.yoloAnnotations || [],
      muraData: location.state?.muraData || { imageDimensions: location.state?.muraData?.imageDimensions, rectangles: location.state?.rectangles || [] },
      issueType: location.state?.issueType || issueType,
      entryId: location.state?.entryId,
    },
  });
};

const handleNext = () => {
  if (selectedCause && issueType) {
    const causeToPass = selectedCause === "Others" && customCause ? customCause : selectedCause;
    navigate("/root-cause-details", {
      state: {
        selectedCauses: [causeToPass],
        muraType: selectedMuraType,
        muraImage,
        uploadedImage: location.state?.uploadedImage,
        recordedEntries: location.state?.recordedEntries,
        previouslySelectedTypes: location.state?.previouslySelectedTypes,
        selectedMuraTypes: location.state?.selectedMuraTypes,
        fromLightLeakage: issueType === "LightLeakage",
        issueType,
        yoloAnnotations: location.state?.yoloAnnotations,
      },
    });
  }
};

  return (
    <div className="min-h-screen bg-black text-white p-4 sm:p-6 md:p-8">
      <div className="flex items-center justify-center p-4 relative">
      
         <div className="absolute left-0">
          <button onClick={handleBack} className="cursor-pointer">
            <ArrowLeft className="w-6 h-6 text-white cursor-pointer" />
          </button>
        </div>
        <h1 className="text-lg sm:text-xl font-semibold">Root Cause Labeling</h1>
      </div>

      <div className="flex items-center gap-4 w-full mb-6">
        <p className="whitespace-nowrap text-sm sm:text-base">Issue Type</p>
        <div className="bg-gray-600 px-3 py-1.5 rounded-lg text-center flex-grow text-sm sm:text-base">
          {selectedMuraType || "Not Selected"}
        </div>
      </div>

      <div className="flex items-center gap-4 w-full mb-6">
        <p className="whitespace-nowrap text-sm sm:text-base">Types</p>
        <select
          value={issueType}
          onChange={handleIssueTypeChange}
          disabled={!isCustomType}
          className={`bg-gray-600 px-3 py-1.5 rounded-lg text-center flex-grow text-sm sm:text-base ${
            !isCustomType ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <option value="" disabled>Select Type</option>
          <option value="MuraLabeling">MuraLabeling</option>
          <option value="LightLeakage">LightLeakage</option>
        </select>
      </div>

      <div className="w-full mb-6 flex justify-center items-center bg-transparent">
        {muraImage && (
          <img
            src={muraImage}
            alt="Mura Image"
            className="max-w-full max-h-[70vh] object-contain rounded-lg border border-gray-700"
          />
        )}
      </div>

      <div className="text-sm sm:text-lg font-bold mb-4"> Root Cause Type</div>
      <div className="space-y-4">
        {muraLabelingCauses.map((cause) => (
          <div key={cause}>
            <label
              className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-300 ${
                selectedCause === cause ? "border-blue-500 bg-gray-700" : "border-gray-600"
              }`}
              onClick={() => handleSelection(cause)}
            >
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center border transition-all duration-300 ${
                  selectedCause === cause ? "border-blue-500" : "border-gray-500"
                }`}
              >
                {selectedCause === cause && <div className="w-3 h-3 bg-blue-500 rounded-full" />}
              </div>
              <span className="ml-2 text-sm sm:text-base">{cause}</span>
            </label>
            {cause === "Others" && selectedCause === "Others" && (
              <input
                type="text"
                value={customCause}
                onChange={(e) => setCustomCause(e.target.value)}
                placeholder="Please Specify"
                className="mt-2 w-full p-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                required
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-between mt-8 w-full">
        <button
          onClick={() => navigate(-1)}
          className="w-[45%] py-2 bg-gray-600 rounded-lg hover:bg-gray-500 text-sm sm:text-base transition-all duration-300"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!selectedCause || (selectedCause === "Others" && !customCause.trim()) || !issueType}
          className={`w-[45%] py-2 rounded-lg text-sm sm:text-base transition-all duration-300 ${
            selectedCause && (selectedCause !== "Others" || customCause.trim()) && issueType
              ? "bg-blue-600 hover:bg-blue-500"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default MuraRootCauseLabeling;
